// The crawlable marketing content shown below the hero on the landing screen:
// how Lumi works, why people choose it, and a FAQ. A STATIC copy of this exact
// markup lives in index.html (inside #app) so search engines that do not run
// JavaScript index the same content -- keep the two in sync, and keep the FAQ
// answers consistent with the FAQPage JSON-LD in index.html. ASCII only.

export const LANDING_INFO_HTML = `
<h2>How Lumi works</h2>
<ol class="info-steps">
  <li><strong>Choose your vibe.</strong> Pick video, audio, or text chat, add an optional name and interests, and confirm your birth year.</li>
  <li><strong>Pass a quick age check.</strong> A private selfie check sorts adults and teens into separate lanes: under-18 users are only ever matched with other under-18 users.</li>
  <li><strong>Light up a conversation.</strong> You are paired with a random person. Chat as long as you like, and skip anytime to meet someone new.</li>
</ol>

<h2>Why people choose Lumi</h2>
<div class="info-grid">
  <div class="info-card">
    <h3>Anonymous by design</h3>
    <p>No account, no profile, and no sign-up required. Your IP address stays hidden behind Lumi's own relay servers, so the person you meet never sees it.</p>
  </div>
  <div class="info-card">
    <h3>Age-safe matching</h3>
    <p>Everyone passes an age check before their first chat, and teens are matched only with other teens. Safety features are never paywalled.</p>
  </div>
  <div class="info-card">
    <h3>Free and ad-free</h3>
    <p>No ads, no trackers, and no data brokers. Lumi is funded by optional extras like <a href="/plus.html">Lumi Plus</a>, never by selling your attention or your data.</p>
  </div>
  <div class="info-card">
    <h3>Video, audio, or text</h3>
    <p>Camera-on is never required. Choose full video, voice-only audio, or plain text chat, whatever feels comfortable today.</p>
  </div>
  <div class="info-card">
    <h3>Group rooms</h3>
    <p>Start a small room and invite friends with a link, or hang out with a few new people at once instead of one-on-one.</p>
  </div>
  <div class="info-card">
    <h3>Real moderation</h3>
    <p>Self-hosted AI moderation and a human review team work together, and you can report anyone in one tap. Abusive behavior leads to bans.</p>
  </div>
</div>

<h2>Who Lumi is for</h2>
<p class="info-lead">
  One place, many reasons to be here. Each page is the honest answer to one situation, in plain words.
</p>
<ul class="info-links">
  <li><a href="/talk-to-someone">Someone to talk to, right now</a></li>
  <li><a href="/practice-conversation">Practice talking to people, at your own pace</a></li>
  <li><a href="/teens">Lumi for teens: a lane without adults</a></li>
  <li><a href="/parents">For parents: how the age check works and what is kept</a></li>
  <li><a href="/new-in-a-country">New in a country</a></li>
  <li><a href="/practice-a-language">Practice speaking a language</a></li>
  <li><a href="/night-owls">Awake at 3 a.m.</a></li>
  <li><a href="/carers">For carers</a></li>
  <li><a href="/over-60s">Over 60</a></li>
  <li><a href="/a-real-person">Talk to a real person, not an AI</a></li>
  <li><a href="/regulars">Regulars: see someone again, without a profile</a></li>
  <li><a href="/quiet-room">The quiet room</a></li>
  <li><a href="/rooms-always-open">Rooms that are always open</a></li>
  <li><a href="/safety">How Lumi keeps you safe, with numbers</a></li>
  <li><a href="/how-lumi-compares">How Lumi compares with random chat sites</a></li>
</ul>

<h2 id="watch-together">Put something on, and watch it together</h2>
<p class="info-age">18+ only</p>
<p class="info-lead">
  Shared video and music are for adults and never appear in teen rooms. In an adult chat, whether a
  room or a one-to-one, anyone can pin a video or a track, and everyone
  watches it at the same moment while you carry on talking. Turn up late and you land where
  everyone else is, not at the beginning.
</p>
<div class="info-grid">
  <div class="info-card">
    <h3>The same moment</h3>
    <p>Where a service allows it the whole room sits on the same second of the same video; where it does not, the room tells you before you start.</p>
  </div>
  <div class="info-card">
    <h3>Turn up late, join where everyone is</h3>
    <p>Arrive halfway through and you start halfway through, at the point the room has reached, rather than watching the first half on your own.</p>
  </div>
  <div class="info-card">
    <h3>The host holds the remote</h3>
    <p>Whoever is hosting can pause, rewind the bit everyone missed, skip an ad or jump ahead for the whole room.</p>
  </div>
  <div class="info-card">
    <h3>Nothing loads until you agree</h3>
    <p>Your browser does not contact the video site at all until you say yes: no player, no thumbnail, no quiet request in the background.</p>
  </div>
  <div class="info-card">
    <h3>Music counts too</h3>
    <p>It is not only films. Put a playlist, a mix, a podcast episode or a talk on and go through it together while you talk over it.</p>
  </div>
  <div class="info-card">
    <h3>Nothing to install</h3>
    <p>An ordinary browser tab on a laptop, a tablet or a phone. No extension, no app store, and no account unless you want one.</p>
  </div>
</div>
<p class="info-note">
  <a href="/watch-together">More about watching together</a>, including which services stay in
  step and which play separately.
</p>
<p class="info-note">
  Hosting a room? You can <a href="/promote-your-room">put it on the front page</a> for a day, a
  weekend, a week, or a month, from $3, and it stays open the whole time.
</p>

<h2 id="rooms-always-open">Rooms that are always open</h2>
<p class="info-lead">
  Some rooms never close. The quiet room is for working, reading, or studying alongside other
  people with your microphone off and nobody expecting you to talk. Language rooms are for
  practicing with other learners. Each has its own short rules card pinned above the chat, and
  you can sit and listen before you say anything. Teens and adults each have their own copy of
  every room, and never share one.
</p>
<p class="info-note">
  <a href="/rooms-always-open">More about the rooms that are always open</a>, including
  <a href="/quiet-room">the quiet room</a>.
</p>

<h2>Frequently asked questions</h2>
<dl class="info-faq">
  <dt>Is Lumi really free?</dt>
  <dd>Yes. Matching, chatting, and every safety feature are free, with no ads. Optional cosmetics and Lumi Plus fund the service.</dd>
  <dt>Do I need an account?</dt>
  <dd>No. You can chat without creating an account. An optional account unlocks extras like Lumi Plus.</dd>
  <dt>How does the age check work?</dt>
  <dd>Lumi's own servers estimate your age group from a few camera frames. No third-party vendors are involved. A person checks the software's decision, and the photo is deleted the moment they have, or after 90 days at the latest. It is used only to keep adults and minors apart, and no face template is ever stored.</dd>
  <dt>Who can use Lumi?</dt>
  <dd>Lumi is for ages 13 and up. Users aged 13 to 17 are matched only with other teens and are never monetized.</dd>
  <dt>Can we watch a video together on Lumi?</dt>
  <dd>Yes, in an adult chat. Anyone in a group room or a one-to-one can pin a video or a track, and everyone watches or listens to it while you carry on talking. People who join partway through land at the point the room has already reached. The host controls the timeline and can skip an ad or jump ahead for the whole room. It runs in the browser with nothing to install. Shared video is not available in teen rooms.</dd>
  <dt>Which services stay in sync when we watch together?</dt>
  <dd>It depends what each service allows. YouTube, Vimeo and SoundCloud keep the whole room on the same moment. Some others follow along more loosely. A few, including Spotify, OK.ru, Mixcloud and TED, play separately for each person, so you press play yourselves. The room says which of these applies before you start, so nobody is left guessing.</dd>
  <dt>What is Lumi Plus?</dt>
  <dd>An optional membership for adults: priority matching, a higher video-quality ceiling, and cosmetic flair. It never changes who you meet. <a href="/plus.html">Learn about Lumi Plus</a>.</dd>
</dl>
`;
